import { drop, take } from 'ramda';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { FieldOptionBase } from 'api/tasks';
import { UserTag } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import { useTranslate, useTranslation } from 'hooks/use-translation';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

export const HoverableUserTag = styled(UserTag)`
  cursor: pointer !important;
`;

export const HoverableTag = styled(Tag)`
  cursor: pointer !important;
`;
const TriggerOption = styled(HoverableTag)`
  max-width: 30ch;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Placeholder = styled.span`
  padding-left: var(--spacing-03);
`;

export interface TriggerOptionProps {
  option: FieldOptionBase<number>;
}

export const UserTriggerOption: FC<TriggerOptionProps> = ({ option }) => (
  <HoverableUserTag {...option} />
);
export const TagTrigggerOption: FC<TriggerOptionProps> = ({
  option: { color, label },
}) => <TriggerOption color={color}>{label}</TriggerOption>;

export type RenderFunction = (
  items: ReactNode[],
  show: number,
  placeholder: ReactNode,
  translate: ReturnType<typeof useTranslate>,
) => ReactNode;

type Props = {
  items: ReactNode[];
  show?: number;
  placeholder?: ReactNode;
  render?: RenderFunction;
};

const defaultRenderFunction: RenderFunction = (
  items,
  show,
  placeholder,
  translate,
) => {
  const count = drop(show)(items).length;
  const other = translate('editor.plus_n_more', { count });

  return (
    <Wrapper>
      {items.length === 0 ? (
        <Placeholder>{placeholder}</Placeholder>
      ) : (
        <>
          {take(show)(items)}
          {count === 0 ? null : (
            <HoverableTag color="cyan">{other}</HoverableTag>
          )}
        </>
      )}
    </Wrapper>
  );
};

export const TriggerContent: FC<Props> = ({
  items,
  show = 2,
  placeholder,
  render = defaultRenderFunction,
}) => {
  const fallback_placeholder = useTranslation('select_with_ellipsis');
  const translate = useTranslate();

  return (
    <>{render(items, show, placeholder ?? fallback_placeholder, translate)}</>
  );
};
